/**
 * Canonical employee-ID discriminated union per CC-07 `documentType`.
 *
 * Phase 03 Plan 03-06 Task 1 (EMP-07). Shipped as the authoritative
 * import site for every caller that needs to validate a
 * `{ documentType, nationalId }` pair. Replaces plan 03-09's inline
 * `employeeIdSchema` stub in `packages/hr-domain/src/bulk-import/row-schema.ts`
 * — plan 03-09 superRefines over `{ documentType, nationalId }` so the import
 * swap is a single-line change; the shape here matches theirs exactly.
 *
 * Five discriminator branches match CC-07 values:
 *   - JNID              → jordanNidSchema
 *   - RESIDENCY_PERMIT  → iqamaSchema
 *   - GCC_ID            → emiratesIdSchema (Luhn-variant check digit)
 *   - PASSPORT          → passportSchema (≥ 6 alnum chars)
 *   - OTHER             → min-1-char free-form
 *
 * The `OTHER` branch exists specifically to pair with the backend
 * `X-LinkHR-Nid-Warning: format-only` header (plan 03-06 Task 2): the tenant
 * can submit any non-empty ID and the server acknowledges with a warning that
 * surfaces as an inline Alert in the profile UI.
 *
 * MUST-test per project conventions §Testing Philosophy — this is the single
 * compile-time contract seam for every ID input across the app.
 */
import { z } from "zod";
import { jordanNidSchema } from "./jordanNid";
import { iqamaSchema } from "./iqama";
import { emiratesIdSchema } from "./emiratesId";
import { passportSchema } from "./passport";

/** Free-form fallback — must be at least 1 character (empty NID is an error). */
export const otherIdSchema = z.string().min(1, { message: "ID value is required" });

/**
 * `{ documentType, nationalId }` discriminated union. Pair with plan 03-09's
 * bulk-import row-schema via `superRefine` — the input shape matches so
 * `idSchema.safeParse({ documentType: row.documentType, nationalId: row.nationalId })`
 * works without adapter code.
 */
export const employeeIdSchema = z.discriminatedUnion("documentType", [
  z.object({
    documentType: z.literal("JNID"),
    nationalId: jordanNidSchema,
  }),
  z.object({
    documentType: z.literal("RESIDENCY_PERMIT"),
    nationalId: iqamaSchema,
  }),
  z.object({
    documentType: z.literal("GCC_ID"),
    nationalId: emiratesIdSchema,
  }),
  z.object({
    documentType: z.literal("PASSPORT"),
    nationalId: passportSchema,
  }),
  z.object({
    documentType: z.literal("OTHER"),
    nationalId: otherIdSchema,
  }),
]);

export type EmployeeIdInput = z.infer<typeof employeeIdSchema>;

/** The five CC-07 documentType literals (runtime list for UI Select options). */
export const EMPLOYEE_DOCUMENT_TYPES = [
  "JNID",
  "RESIDENCY_PERMIT",
  "GCC_ID",
  "PASSPORT",
  "OTHER",
] as const;

export type EmployeeDocumentType = (typeof EMPLOYEE_DOCUMENT_TYPES)[number];
